import { customElement } from 'lit/decorators.js';
import { last, merge, find, orderBy } from 'lodash-es';
import { formatNumber } from '@kisters/wiski-web/common';
import dayjs from '@kisters/wiski-web/common/dayjsext';
import KiWwpGraphPrecip from '@kisters/wiski-web/components/ki-wwp-graph/ki-wwp-graph-precip';

@customElement('wwp-graph-precip-hdoe')
export default class KiWwpGraphPrecipHdoe extends KiWwpGraphPrecip {
  
      drawSeries(period, min, max) {
    const cfg = find(this.ranges, { value: period });
    let _min = min;
    let _max = max;
    if (cfg.yearSelector) {
      if (!_min && !_max) {
        const value = this.selectedYear;

        _min =
          value && value !== 'YTD'
            ? dayjs(value, 'YYYY').tz().startOf('year').valueOf()
            : dayjs().tz().startOf('year').valueOf();

        _max =
          value && value !== 'YTD'
            ? dayjs(value, 'YYYY').tz().endOf('year').valueOf()
            : dayjs().tz().valueOf();
      }
      this.renderRoot
        .querySelector('#yearselector')
        .classList.remove('hideButton');
    } else {
      this.renderRoot
        .querySelector('#yearselector')
        .classList.add('hideButton');
    }
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    if (this.files[0][cfg.data]) {
      const fileData = this.files[0][cfg.data][0]
        ? this.files[0][cfg.data][0]
        : this.files[0][cfg.data];
      // Full-record / year view: fit the axis to the data's own extent, padded
      // by half the bucket spacing so the first/last columns (e.g. monthly bars)
      // aren't clipped at the axis edges, ending at the last bucket that has a
      // value (avoid an empty trailing month). Shorter periods keep their
      // rolling [now - period, now] window.
      if ((period === 'PoR' || period === 'P1Y') && !_min && !_max) {
        const first = dayjs(fileData.data[0][0]).valueOf();
        const step = fileData.data[1]
          ? dayjs(fileData.data[1][0]).valueOf() - first
          : 0;
        const lastWithValue =
          [...fileData.data].reverse().find(p => p[1] != null) ||
          last(fileData.data);
        _min = first - step / 2;
        _max = dayjs(lastWithValue[0]).valueOf() + step / 2;
      }

      const minDate =
        _min || dayjs(_max).subtract(dayjs.duration(period)).valueOf();
      const maxDate = _max || dayjs().valueOf();
      const xAxis = this.chart.get('mainXAxis');
      this.chart.zoomOut();

      /*  // Workaround for year view (avoid plotting of empty next month)
      if (cfg.value === 'P1Y')
        xAxis.setExtremes(
          minDate,
          max || dayjs().startOf('month'),
          false,
          false,
        ); 
      else */

      xAxis.setExtremes(minDate, maxDate, false, false);

      if (cfg.yAxisLabel) {
        this.yAxisLabel = cfg.yAxisLabel;
        this.chart.get('mainYAxis').setTitle({ text: cfg.yAxisLabel }, false);
      }

      this.chart.get('sumYAxis').update({ visible: !cfg.sumOnMainAxis }, false);

      const timeformat = cfg.dateFormat || '%d.%m.%Y %H:%M';
      let ts = find(this.files[0][cfg.data], { ts_shortname: cfg.ts });
      ts =
        ts || this.files[0][cfg.data][0]
          ? this.files[0][cfg.data][0]
          : this.files[0][cfg.data];

      if (
        this.filterAccuracy &&
        ts.columns.indexOf('Aggregation Accuracy %') > -1
      ) {
        const { columns } = ts;
        const valueIndex = columns.indexOf('Value');
        const accIndex = columns.indexOf('Aggregation Accuracy %');
        ts.data.forEach(item => {
          if (item[accIndex] < this.filterAccuracy) {
            item[valueIndex] = null;
          }
        });
      }
      const tsdata = this._formatGraphData(ts.data, minDate, maxDate);
      const accData = tsdata;
      const tableData = orderBy(tsdata, 'x', ['desc']).map(item => ({
        timestamp: dayjs(item.x)
          .tz()
          .format(cfg.dateFormatTable || 'L LT'),
        value: formatNumber(item.y, this.numberFormat || {}),
      }));
      const table = this.shadowRoot.getElementById('dataTable');
      table.columns = this.tablecolumns;
      // eslint-disable-next-line no-multi-assign
      this.orderedTableData = table.data = tableData;
      table.data = this.orderedTableData;

      // Show Accuracy in tooltip
      if (this.filterAccuracy) {
        this._addAccuracy(accData, timeformat);
      }

      // Add bars
      this.chart.addSeries(
        {
          data: tsdata,
          name: cfg.tslabel || this.parameter_label,
          id: 'data',
          color: '#0056a0',
          selected: true,
          pointPlacement: this.pointPlacement,
          pointPadding: 0,
          marker: { enabled: false },
          tooltip: merge(
            {
              valueDecimals: 1,
              valueSuffix: ` ${ts.ts_unitsymbol}`,
              xDateFormat: timeformat,
            },
            this.tooltipBar,
          ),
        },
        false,
      );

      // Add sum line
      if (!cfg.hideSumLine) {
        this.chart.addSeries(
          {
            data: this._formatGraphDataSum(
              ts.data,
              minDate,
              maxDate,
              cfg.resetSumAt,
            ),
            name: cfg.sumLabel || this.sumLabel || this.i18n.t('Sum line'),
            yAxis: cfg.sumOnMainAxis ? 'mainYAxis' : 'sumYAxis',
            type: 'line',
            step: 'left',
            marker: { enabled: false },
            color: 'rgb(170,70,67)',
            selected: true,
            tooltip: merge(
              {
                valueDecimals: 1,
                valueSuffix: ` ${ts.ts_unitsymbol}`,
                xDateFormat: timeformat,
              },
              this.tooltipSum,
            ),
          },
          false,
        );
      }
      this.chart.redraw();
    }
  }
}